import { useEffect, useMemo, useState } from 'react';

import {
  URL,
  EXTENSION,
  SIZE,
  WIDTH,
  HEIGHT,
  ORIENTATION,
  VIDEO_LENGTH,
} from 'scenes/Gallery/components/File/constants';
import {
  UPLOAD_FILE_IMAGE,
  UPLOAD_FILE_VIDEO,
  UPLOAD_FILE_ZIP,
} from 'scenes/Gallery/GalleryPage/constants';

const typesMapping = {
  'image/jpeg': UPLOAD_FILE_IMAGE,
  'image/jpg': UPLOAD_FILE_IMAGE,
  'image/png': UPLOAD_FILE_IMAGE,
  'image/gif': UPLOAD_FILE_IMAGE,
  'video/mp4': UPLOAD_FILE_VIDEO,
  'video/quicktime': UPLOAD_FILE_VIDEO,
  'application/zip': UPLOAD_FILE_ZIP,
  'application/x-zip-compressed': UPLOAD_FILE_ZIP,
  'multipart/x-zip': UPLOAD_FILE_ZIP,
};

const FILE_LIMIT = 1024 * 1024 * 100;

const LANDSCAPE = 'Landscape';
const PORTRAIT = 'Portrait';

const useFileMetadata = ({ file }) => {
  const [fileAttributes, setFileAttributes] = useState({});

  const uploadType = useMemo(() => {
    return typesMapping[file?.type];
  }, [file]);

  const fileOverSize = useMemo(() => {
    return uploadType === UPLOAD_FILE_VIDEO && file?.size >= FILE_LIMIT;
  }, [uploadType, file]);

  useEffect(() => {
    if (!file) {
      return;
    }

    setFileAttributes((old) => ({
      ...old,
      [EXTENSION]: `.${file.name.split('.').pop()}`,
      [SIZE]: file.size,
    }));

    if (fileOverSize) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setFileAttributes((old) => ({
        ...old,
        [URL]: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  }, [file, fileOverSize]);

  useEffect(() => {
    if (!fileAttributes[URL]) {
      return;
    }

    switch (uploadType) {
      case UPLOAD_FILE_IMAGE:
        const tmpImage = new Image();
        tmpImage.src = fileAttributes[URL];
        tmpImage.onload = function () {
          setFileAttributes((old) => ({
            ...old,
            [WIDTH]: tmpImage.width,
            [HEIGHT]: tmpImage.height,
            [ORIENTATION]: `${
              tmpImage.width > tmpImage.height ? LANDSCAPE : PORTRAIT
            }`,
          }));
        };
        break;
      case UPLOAD_FILE_VIDEO:
        const video = document.createElement('video');
        video.addEventListener('loadedmetadata', () => {
          setFileAttributes((old) => ({
            ...old,
            [WIDTH]: video.videoWidth,
            [HEIGHT]: video.videoHeight,
            [ORIENTATION]: `${
              video.videoWidth > video.videoHeight ? LANDSCAPE : PORTRAIT
            }`,
            [VIDEO_LENGTH]: Math.round(video.duration),
          }));
        });
        video.src = fileAttributes[URL];
        break;
      default:
        break;
    }
  }, [fileAttributes[URL]]);

  return {
    uploadType,
    fileOverSize,
    ...fileAttributes,
  };
};

export default useFileMetadata;
